import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// =============================================================================
// API Keys
// =============================================================================

export interface APIKey {
  id: number
  name: string
  key_prefix: string
  scopes: string[]
  is_active: boolean
  last_used_at: string | null
  created_at: string
}

export interface CreatedAPIKey {
  id: number
  name: string
  key: string
  secret: string
  key_prefix: string
}

export interface CreateAPIKeyInput {
  name: string
  scopes: string[]
}

export function useAPIKeys() {
  return useQuery({
    queryKey: ['integration-api-keys'],
    queryFn: async () => {
      const { data } = await api.get<APIKey[]>('/api/integrations/keys/')
      return data
    },
  })
}

export function useCreateAPIKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAPIKeyInput) => {
      const { data } = await api.post<CreatedAPIKey>(
        '/api/integrations/keys/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-api-keys'] })
    },
  })
}

export function useUpdateAPIKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number
      patch: Partial<Pick<APIKey, 'is_active' | 'name' | 'scopes'>>
    }) => {
      await api.patch(`/api/integrations/keys/${id}/`, patch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-api-keys'] })
    },
  })
}

export function useDeleteAPIKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/integrations/keys/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-api-keys'] })
    },
  })
}

// =============================================================================
// Webhooks
// =============================================================================

export interface Webhook {
  id: number
  name: string
  url: string
  events: string[]
  events_display?: Record<string, string>
  is_active: boolean
  last_triggered_at: string | null
  failure_count: number
  created_at: string
}

export interface CreateWebhookInput {
  name: string
  url: string
  events: string[]
}

export function useWebhooks() {
  return useQuery({
    queryKey: ['integration-webhooks'],
    queryFn: async () => {
      const { data } = await api.get<Webhook[]>('/api/integrations/webhooks/')
      return data
    },
  })
}

export function useCreateWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateWebhookInput) => {
      const { data } = await api.post<Webhook>(
        '/api/integrations/webhooks/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-webhooks'] })
    },
  })
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number
      patch: Partial<Pick<Webhook, 'is_active' | 'name' | 'url' | 'events'>>
    }) => {
      await api.patch(`/api/integrations/webhooks/${id}/`, patch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-webhooks'] })
    },
  })
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/integrations/webhooks/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-webhooks'] })
    },
  })
}

/** Le secret de signature d'un webhook : demandé seulement quand le gérant l'affiche. */
export function useWebhookSecret() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.get<{ signing_secret: string }>(
        `/api/integrations/webhooks/${id}/secret/`,
      )
      return data.signing_secret
    },
  })
}

/** What the customer's endpoint answered to the test event. */
export interface WebhookTestResult {
  success: boolean
  reason: 'ok' | 'http' | 'timeout' | 'unreachable' | 'url_refused' | 'error'
  http_status: number | null
  detail: string
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<WebhookTestResult>(
        `/api/integrations/webhooks/${id}/test/`,
      )
      return data
    },
  })
}

export const WEBHOOK_EVENTS = [
  'call.started',
  'call.answered',
  'call.completed',
  'call.missed',
  'call.summary_ready',
  'call.recording_ready',
  'voicemail.received',
  'sms.received',
  'sms.sent',
  'sms.delivered',
  'sms.failed',
  'contact.created',
  'contact.updated',
  'contact.deleted',
  'appointment.booked',
  'appointment.changed',
  'task.created',
] as const
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number]

// =============================================================================
// Widget marque blanche (la voix et les textos dans le logiciel d'un partenaire)
// =============================================================================

export type WidgetEmbarqueStatus = 'off' | 'demande' | 'actif'

export interface WidgetEmbarqueState {
  status: WidgetEmbarqueStatus
  requested_at: string | null
  phone_verification_required: boolean
  auto_create_users: boolean
  forward_to_mobile: boolean
  seat_limit: number | null
  linked_users: number
  allowed_origins: string[]
}

export type WidgetEmbarquePatch = Partial<
  Pick<WidgetEmbarqueState, 'phone_verification_required' | 'auto_create_users' | 'forward_to_mobile' | 'seat_limit' | 'allowed_origins'>
>

export function useWidgetEmbarque() {
  return useQuery({
    queryKey: ['widget-embarque'],
    queryFn: async () => {
      const { data } = await api.get<WidgetEmbarqueState>('/api/integrations/widget-embarque/')
      return data
    },
  })
}

export function useRequestWidgetEmbarque() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<WidgetEmbarqueState>('/api/integrations/widget-embarque/request/')
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(['widget-embarque'], data),
  })
}

export function useUpdateWidgetEmbarque() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: WidgetEmbarquePatch) => {
      const { data } = await api.patch<WidgetEmbarqueState>('/api/integrations/widget-embarque/', patch)
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(['widget-embarque'], data),
  })
}
